import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { PageResponse, Pagination } from '@core/shared/domain';
import { QueryParams } from '@core/shared/util';

import { AddShoppingCartPosition, CostCenterForSelection, UpdateShoppingCartPosition } from '../../common';
import {
  AddShoppingCartPositionResult,
  ConfirmDeletedPosition,
  DeleteShoppingCartPositionResult,
  RequestShoppingCartResult,
  ShoppingCart,
  ShoppingCartDeletedPosition,
  ShoppingCartPosition,
  UpdateShoppingCartPositionResult,
} from '../models';

@Injectable()
export class ShoppingCartService {
  private readonly baseUrl = '/api/medical-shop2/shoppingcarts';

  constructor(private readonly http: HttpClient) {}

  // TODO: Update this after the split of the endpoint
  /**
   * @returns the cost centers for the selection in the list (menu and flyout; will be separated later)
   */
  fetchCostCenters(): Observable<Array<CostCenterForSelection>> {
    return this.http.get<Array<CostCenterForSelection>>(`${this.baseUrl}/costcenters`);
  }

  /**
   * Adds a selected article to a selected cost center or shopping cart
   * @param addedToCart An object containing the selected amount of an article and the cost center/shopping cart with its address
   * @returns The data of the (newly created) cart and the position. Will be displayed in a snackbar.
   */
  addArticleToCart(addedToCart: AddShoppingCartPosition): Observable<AddShoppingCartPositionResult> {
    return this.http.post<AddShoppingCartPositionResult>(`${this.baseUrl}/positions`, addedToCart);
  }

  /**
   * @returns the shopping carts for the shopping cart page
   */
  fetchShoppingCarts(): Observable<Array<ShoppingCart>> {
    return this.http.get<Array<ShoppingCart>>(this.baseUrl);
  }

  /**
   * @param shoppingCartRecordId The id of the shopping cart
   * @param pagination The pagination data
   * @returns the positions of the selected shpping cart
   */
  fetchShoppingCartPositions(
    shoppingCartRecordId: string,
    pagination: Partial<Pagination> = {},
  ): Observable<PageResponse<ShoppingCartPosition>> {
    const { page = 1, pageSize = 50 } = pagination;
    const params = QueryParams.build<ShoppingCartPosition>().page(page).pageSize(pageSize).toHttpParams();

    return this.http.get<PageResponse<ShoppingCartPosition>>(`${this.baseUrl}/${shoppingCartRecordId}/positions`, {
      params,
    });
  }

  /**
   * Requests the selected shopping cart
   * @param shoppingCartId The id of the shopping cart
   * @returns none
   */
  requestShoppingCart(shoppingCartId: string): Observable<RequestShoppingCartResult> {
    return this.http.post<RequestShoppingCartResult>(`${this.baseUrl}/${shoppingCartId}/request`, {});
  }

  /**
   * Update the quantity of a position in a shopping cart with an absolute quantity
   * @param shoppingCartId The id of the shopping cart
   * @param positionId The globalArticleId of the updated position
   * @param updateDto the updated quantity
   * @returns The updated data for the shopping cart header and the position
   */
  updateCartPositionQuantity(
    shoppingCartId: string,
    positionId: string,
    updateDto: UpdateShoppingCartPosition,
  ): Observable<UpdateShoppingCartPositionResult> {
    return this.http.put<UpdateShoppingCartPositionResult>(
      `${this.baseUrl}/${shoppingCartId}/positions/${positionId}`,
      updateDto,
    );
  }

  /**
   * Removes a position from the shopping cart
   * @param shoppingCartId The id of the shopping cart
   * @param positionId The ID of the position
   * @returns The updated data of the shopping cart header
   */
  deleteCartPosition(shoppingCartId: string, positionId: string): Observable<DeleteShoppingCartPositionResult> {
    return this.http.delete<DeleteShoppingCartPositionResult>(
      `${this.baseUrl}/${shoppingCartId}/positions/${positionId}`,
    );
  }

  /**
   * Fetches all as deleted marked positions of a shopping cart
   * @param shoppingCartId The id of the shopping cart
   * @returns All as deleted marked positions of the shopping cart
   */
  fetchDeletedPositions(shoppingCartId: string): Observable<Array<ShoppingCartDeletedPosition>> {
    return this.http.get<Array<ShoppingCartDeletedPosition>>(`${this.baseUrl}/${shoppingCartId}/deleted-positions`);
  }

  /**
   * Removes the deleted position from the shopping cart
   * @param param0 <{shoppingCartId, positionId}> The ids of the shopping cart and the position that will be removed
   * @returns The updated shopping cart or null if the cart is empty after the deletion
   */
  removeDeletedPosition({
    shoppingCartId,
    positionId,
  }: ConfirmDeletedPosition): Observable<DeleteShoppingCartPositionResult> {
    return this.http.delete<DeleteShoppingCartPositionResult>(
      `${this.baseUrl}/${shoppingCartId}/deleted-positions/${positionId}`,
    );
  }
}
